import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspaceMembership } from "@/hooks/useWorkspace";
import WorkspaceRemovedScreen from "./WorkspaceRemovedScreen";

const LOGGING_OUT_KEY = "dailyfi-logging-out";
const RENEWAL_REASONS = new Set(["grace_period", "suspended", "purged", "expired"]);

interface Props {
  children: React.ReactNode;
  requireTermsAcceptance?: boolean;
}

export default function ProtectedRoute({ children }: Props) {
  const { user, access, loading } = useAuth();
  const location = useLocation();
  const membership = useWorkspaceMembership();

  useEffect(() => {
    if (loading) return;

    if (!user) {
      if (window.sessionStorage.getItem(LOGGING_OUT_KEY) === "1") {
        window.sessionStorage.removeItem(LOGGING_OUT_KEY);
        window.location.replace('/app?login=1');
        return;
      }

      const next = `${location.pathname}${location.search}${location.hash}`;
      const target = `/app?login=1&next=${encodeURIComponent(next)}`;
      window.location.replace(target);
      return;
    }

    if (access && !access.is_authorized && RENEWAL_REASONS.has(access.reason)) {
      window.location.replace("/app/renovar");
    }
  }, [access, loading, location.hash, location.pathname, location.search, user]);

  if (loading || membership.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (access && !access.is_authorized && RENEWAL_REASONS.has(access.reason)) {
    return null;
  }

  // Check if user was removed from workspace
  if (membership.data?.removed && membership.data.removedAt) {
    return (
      <WorkspaceRemovedScreen
        removedAt={membership.data.removedAt}
        removedReason={membership.data.removedReason}
      />
    );
  }

  return <>{children}</>;
}
